import { fabric } from "fabric";
import { v4 } from "uuid";
import { CanvasHandler } from "../../handlers";
import { FabricEvent, FabricObject } from "../../types/FabricCustomType";
import { IObjectDesingner } from "../../types/HandlerObjectType";
import { ShapeProperties } from "./properties";
import { iconStage } from "../../constants/Icons"

class Triangle extends ShapeProperties implements IObjectDesingner {
  constructor(canvasHandler: CanvasHandler) {
    super(canvasHandler);
  }
  onRotated(event: FabricEvent) {
    throw new Error("Method not implemented.");
  }

  public onInit() {
    this.canvasHandler.interactionHandler.drawing("triangle", "crosshair");
    this.canvasHandler.activeShape = null;
  }

  onMouseDblClick(event: FabricEvent<Event>) {
    return;
  }

  public onMousedown(event: FabricEvent<MouseEvent>) {
    const { x, y } = this.canvasHandler.canvas.getPointer(event.e);
    this.addPoint(x, y);
  }

  public onMousemove(event: FabricEvent<MouseEvent>) {
    const { x, y } = this.canvasHandler.canvas.getPointer(event.e);
    if (!this.canvasHandler.isDown) return;
    this.updating(x, y);
    this.canvasHandler.canvas.renderAll();
  }

  public onMouseup(event: FabricEvent<MouseEvent>) {
    const { x, y } = this.canvasHandler.canvas.getPointer(event.e);
    this.generate(false, false, x, y);
  }

  private addPoint(x: number, y: number) {
    this.canvasHandler.isDown = true;
    const editable = this.canvasHandler.editable;
    const id = v4();
    this.canvasHandler.activeShape = new fabric.Triangle({
      name: "New triangle",
      left: x,
      top: y,
      originX: "left",
      originY: "top",
      width: 0,
      height: 0,
      angle: 0,
      fill: "#cccccc",
      strokeWidth: 4,
      stroke: "#727272",
      strokeUniform: true,
      transparentCorners: false,
      opacity: 1,
      objectCaching: !editable,
      type: "triangle",
      hasControls: editable,
      hasBorders: editable,
      selectable: editable,
      lockMovementX: !editable,
      lockMovementY: !editable,
      hoverCursor: !editable ? "pointer" : "move",
    }) as FabricObject<fabric.Triangle>;

    this.canvasHandler.activeShape.set({
      id: id,
      rx: 4,
      ry: 4,
      superType: "triangle",
      container: this.canvasHandler.container.id,
      editable: editable,
      text: "Label",
      displayedLabel: false,
      typeIcon: "",
    });

    this.canvasHandler.objText = new fabric.Text("Label", {
      fontSize: 16,
      left: x,
      top: y,
      angle: 0,
      fill: "#727272",
      stroke: "#727272",
      strokeWidth: 0,
      fontFamily: "Roboto",
      strokeUniform: true,
      opacity: 1,
      type: "textbox",
      hasControls: editable,
      hasBorders: editable,
      selectable: editable,
      lockMovementX: !editable,
      lockMovementY: !editable,
      hoverCursor: !editable ? "pointer" : "move",
      text: "?",
      visible: false
    }) as FabricObject<fabric.Text>;

    this.canvasHandler.objText.set({
      id: id,
      superType: "textbox",
      container: this.canvasHandler.container.id,
      editable,
      displayedLabel: false,
    });

    let svgConverted, serializer, svgStr;

    let svgEl = iconStage;

    svgConverted = document.createRange().createContextualFragment(svgEl);
    serializer = new XMLSerializer();
    svgStr = serializer.serializeToString(svgConverted);

    let containerId = this.canvasHandler.container.id;
    let loadedObject = this.canvasHandler.objIcon;
    fabric.loadSVGFromString(svgStr, function (objects, options) {
      // Group elements to fabric.PathGroup (more than 1 elements) or
      // to fabric.Path
      loadedObject = fabric.util.groupSVGElements(objects, options);

      loadedObject.set({
        id: id,
        hasControls: editable,
        hasBorders: editable,
        selectable: editable,
        lockMovementX: !editable,
        lockMovementY: !editable,
        hoverCursor: !editable ? "pointer" : "move",
        container: containerId,
        editable: editable,
        fill: "#727272",
        left: x,
        top: y,
        originX: "left",
        originY: "top",
        opacity: 1,
        objectCaching: !editable,
        superType: "svg",
        type: "svg",
        name: "New icon SVG",
        svg: svgEl,
        loadType: "svg",
        visible: false,
        scaled: 1,
        originHeight: loadedObject.height,
        originWidth: loadedObject.width,
        rotated: 0
      });
    });

    this.canvasHandler.activeShape.guides = {};
    this.canvasHandler.canvas.add(this.canvasHandler.activeShape);
    this.canvasHandler.objIcon = loadedObject;
  }

  private updating(x: number, y: number) {
    if (this.canvasHandler.originPointer.x > x)
      this.canvasHandler.activeShape.set({ left: x });
    if (this.canvasHandler.originPointer.y > y)
      this.canvasHandler.activeShape.set({ top: y });
    this.canvasHandler.activeShape.set({
      width: Math.abs(this.canvasHandler.originPointer.x - x),
    });
    this.canvasHandler.activeShape.set({
      height: Math.abs(this.canvasHandler.originPointer.y - y),
    });
  }

  private generate(isDown: any, loaded: any, x: number, y: number) {
    const { editable, onAdd } = this.canvasHandler;
    const id = v4();

    let group = new fabric.Group(
      [this.canvasHandler.activeShape, this.canvasHandler.objText, this.canvasHandler.objIcon],
      {
        left: this.canvasHandler.originPointer.x < x ? this.canvasHandler.originPointer.x : x,
        top: this.canvasHandler.originPointer.y < y ? this.canvasHandler.originPointer.y : y,
        hasControls: editable,
        hasBorders: editable,
        type: "group",
        subTargetCheck: true,
      }
    ) as FabricObject<fabric.Group>;
    group.set({
      id: id,
      superType: "triangle",
      visibleIcon: false,
      visibleText: false,
      hoverCursor: "crosshair",
      selectable: false
    });

    group.setControlsVisibility({
      tl: false, //top-left
      mt: false, // middle-top
      tr: false, //top-right
      ml: false, //middle-left
      mr: false, //middle-right
      bl: false, // bottom-left
      mb: false, //middle-bottom
      br: false, //bottom-right
      mtr: true,
    });
    this.canvasHandler.canvas.remove(this.canvasHandler.activeShape);
    this.canvasHandler.canvas.add(group);

    group.item(2).left = 0;
    group.item(2).top = 0;

    const widthIcon = group.item(2).width ?? 0;
    const heightIcon = group.item(2).height ?? 0;
    group.item(2).set({
      left: group.item(2).left - (widthIcon / 2),
      top: group.item(2).top - (heightIcon / 2),
      fill: "#727272",
    });

    group.item(1).left = 1;
    group.item(1).top = 1;

    const widthText = group.item(1).width ?? 0;
    const heightText = group.item(1).height ?? 0;

    group.item(1).set({
      left: group.item(1).left - (widthText / 2),
      top: group.item(1).top - (heightText / 2),
    });

    if (!this.canvasHandler.transactionHandler.active && !loaded)
      this.canvasHandler.transactionHandler.save("add");
    if (onAdd && editable && !loaded) onAdd(group);

    this.canvasHandler.isDown = isDown;
    this.canvasHandler.canvas.discardActiveObject();
    this.canvasHandler.activeShape = null;
    this.canvasHandler.canvas.renderAll();
  }
}
export default Triangle;
